import { useState, type FormEvent } from "react";
import { films } from "@/mocks/films";
import { postForm } from "@/utils/form";
import type { Film } from "@/types/content";

const SUBMIT_URL = "https://readdy.ai/api/form/db74hjbaskivmpnrcfqg";

const initialState = { name: "", phone: "", film: "", date: "", people: "2", note: "" };

export default function ReservationForm() {
  const [form, setForm] = useState(initialState);
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [feedback, setFeedback] = useState("");

  const update = (key: keyof typeof initialState, value: string) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formEl = event.currentTarget;
    if (!form.name.trim() || !form.phone.trim()) {
      setStatus("error");
      setFeedback("请填写姓名与联系电话");
      return;
    }
    setStatus("submitting");
    setFeedback("");
    try {
      const result = await postForm(SUBMIT_URL, formEl, "company_alt");
      if (result.ok) {
        setStatus("success");
        setFeedback(result.message || "预约申请已提交成功");
        setForm(initialState);
      } else {
        setStatus("error");
        setFeedback(result.message);
      }
    } catch {
      setStatus("error");
      setFeedback("网络异常，提交失败，请稍后重试");
    }
  };

  return (
    <form
      id="reservationForm"
      data-readdy-form
      onSubmit={handleSubmit}
      className="p-6 md:p-8 rounded-lg bg-background-100 border border-background-200/70"
    >
      <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 mb-2">预约观影</h2>
      <p className="text-sm text-foreground-600 mb-6 leading-relaxed">
        填写你的观影意向，我们会尽快与你确认场次与座位。提交后请以工作人员回复为准。
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="res-name" className="block text-xs text-foreground-500 mb-1.5">
            姓名 <span className="text-accent-500">*</span>
          </label>
          <input
            id="res-name"
            name="name"
            value={form.name}
            onChange={(e) => update("name", e.target.value)}
            placeholder="请输入姓名"
            className="w-full px-3 py-2.5 rounded-md bg-background-50 border border-background-200/70 text-sm text-foreground-950 placeholder:text-foreground-500 outline-none focus:border-accent-500/70"
          />
        </div>
        <div>
          <label htmlFor="res-phone" className="block text-xs text-foreground-500 mb-1.5">
            联系电话 <span className="text-accent-500">*</span>
          </label>
          <input
            id="res-phone"
            name="phone"
            type="tel"
            value={form.phone}
            onChange={(e) => update("phone", e.target.value)}
            placeholder="请输入手机号"
            className="w-full px-3 py-2.5 rounded-md bg-background-50 border border-background-200/70 text-sm text-foreground-950 placeholder:text-foreground-500 outline-none focus:border-accent-500/70"
          />
        </div>
        <div>
          <label htmlFor="res-film" className="block text-xs text-foreground-500 mb-1.5">
            意向影片
          </label>
          <select
            id="res-film"
            name="film"
            value={form.film}
            onChange={(e) => update("film", e.target.value)}
            className="w-full px-3 py-2.5 rounded-md bg-background-50 border border-background-200/70 text-sm text-foreground-950 outline-none focus:border-accent-500/70 cursor-pointer"
          >
            <option value="">请选择影片</option>
            {(films as Film[]).map((film) => (
              <option key={film.id} value={film.title}>
                {film.title}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="res-date" className="block text-xs text-foreground-500 mb-1.5">
            意向日期
          </label>
          <input
            id="res-date"
            name="date"
            type="date"
            value={form.date}
            onChange={(e) => update("date", e.target.value)}
            className="w-full px-3 py-2.5 rounded-md bg-background-50 border border-background-200/70 text-sm text-foreground-950 outline-none focus:border-accent-500/70 cursor-pointer"
          />
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="res-people" className="block text-xs text-foreground-500 mb-1.5">
            观影人数
          </label>
          <select
            id="res-people"
            name="people"
            value={form.people}
            onChange={(e) => update("people", e.target.value)}
            className="w-full px-3 py-2.5 rounded-md bg-background-50 border border-background-200/70 text-sm text-foreground-950 outline-none focus:border-accent-500/70 cursor-pointer"
          >
            {["1", "2", "3", "4", "5", "6+"].map((n) => (
              <option key={n} value={n}>
                {n} 人
              </option>
            ))}
          </select>
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="res-note" className="block text-xs text-foreground-500 mb-1.5">
            备注（选填，最多 500 字）
          </label>
          <textarea
            id="res-note"
            name="note"
            value={form.note}
            maxLength={500}
            rows={3}
            onChange={(e) => update("note", e.target.value)}
            placeholder="例如：需要前排座位、有无儿童同行等"
            className="w-full px-3 py-2.5 rounded-md bg-background-50 border border-background-200/70 text-sm text-foreground-950 placeholder:text-foreground-500 outline-none focus:border-accent-500/70 resize-none"
          />
        </div>
      </div>

      <input
        type="text"
        name="company_alt"
        className="form-extra-field"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
      />

      {feedback && (
        <p
          className={`mt-4 text-sm ${status === "success" ? "text-accent-500" : "text-primary-700"}`}
        >
          <i className={status === "success" ? "ri-checkbox-circle-line mr-1" : "ri-error-warning-line mr-1"} />
          {feedback}
        </p>
      )}

      <button
        type="submit"
        disabled={status === "submitting"}
        className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-md bg-accent-500 text-background-50 font-label font-medium hover:bg-accent-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
      >
        {status === "submitting" ? (
          <>
            <i className="ri-loader-4-line animate-spin" />
            提交中…
          </>
        ) : (
          <>
            <i className="ri-send-plane-line" />
            提交预约
          </>
        )}
      </button>
    </form>
  );
}